import React, { useMemo, useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import { slotToLabel } from "../lib/time";
import { Badge } from "./ui/badge";

const ROW_H = 38;
const LABEL_W = 216;
const ZOOM_LEVELS = [8, 11, 14, 20, 28];

function cellStyle(status, priority) {
  if (status === "idle") return { backgroundColor: "transparent" };
  if (status === "covered") return { backgroundColor: "rgba(48,209,88,0.28)" };
  const alpha = { 1: 0.9, 2: 0.7, 3: 0.5, 4: 0.38, 5: 0.28 }[priority] || 0.4;
  return { backgroundColor: `rgba(255,69,58,${alpha})` };
}

export function CoverageHeatmap({ config, workstations, schedule, day }) {
  const [zoom, setZoom] = useState(2);
  const CELL_W = ZOOM_LEVELS[zoom];
  const N = config.slots_per_day;
  const grid = (schedule?.coverage_grid || {})[String(day)] || {};
  const rows = useMemo(
    () => workstations.filter((w) => grid[w.id] && grid[w.id].some((s) => s !== "idle")),
    [workstations, grid]
  );

  const hourTicks = [];
  for (let t = 0; t < N; t++) if ((config.day_start_min + t * config.slot_min) % 60 === 0) hourTicks.push(t);

  return (
    <div>
      <div className="flex items-center justify-end mb-2 gap-1">
        <button data-testid="heatmap-zoom-out" onClick={() => setZoom((z) => Math.max(0, z - 1))}
          className="w-8 h-8 flex items-center justify-center rounded-sm border border-line text-muted hover:text-ink hover:border-lineFocus transition-colors">
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className="mono text-[10px] text-faint w-8 text-center">{CELL_W}px</span>
        <button data-testid="heatmap-zoom-in" onClick={() => setZoom((z) => Math.min(ZOOM_LEVELS.length - 1, z + 1))}
          className="w-8 h-8 flex items-center justify-center rounded-sm border border-line text-muted hover:text-ink hover:border-lineFocus transition-colors">
          <ZoomIn className="w-4 h-4" />
        </button>
      </div>

      <div className="overflow-auto border border-line rounded-sm bg-surface" data-testid="coverage-heatmap">
        <div style={{ minWidth: LABEL_W + N * CELL_W }}>
          <div className="flex sticky top-0 z-20 bg-surface2 border-b border-line">
            <div className="sticky left-0 z-30 bg-surface2 border-r border-line flex items-center px-3" style={{ width: LABEL_W, height: 32 }}>
              <span className="mono text-[10px] uppercase tracking-widest text-muted">Workstation</span>
            </div>
            <div className="relative" style={{ width: N * CELL_W, height: 32 }}>
              {hourTicks.map((t) => (
                <div key={t} className="absolute top-0 h-full flex items-center border-l border-line" style={{ left: t * CELL_W }}>
                  <span className="mono text-[10px] text-muted pl-1">{slotToLabel(config, t)}</span>
                </div>
              ))}
            </div>
          </div>

          {rows.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-muted">No coverage data for this day — generate a schedule first.</div>
          )}

          {rows.map((w) => {
            const row = grid[w.id] || [];
            return (
              <div key={w.id} className="flex border-b border-line group" data-testid={`heatmap-row-${w.id}`}>
                <div className="sticky left-0 z-10 bg-surface group-hover:bg-surface2 border-r border-line flex items-center justify-between gap-2 px-3 transition-colors duration-150" style={{ width: LABEL_W, height: ROW_H }}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: w.color }} />
                    <span className="text-xs font-medium text-ink truncate">{w.name}</span>
                  </div>
                  <Badge tone={w.priority} className="shrink-0">P{w.priority}</Badge>
                </div>
                <div className="relative" style={{ width: N * CELL_W, height: ROW_H }}>
                  {row.map((status, t) => (
                    <div key={t} data-testid={`heatmap-cell-${w.id}-${t}`}
                      title={`${w.name} · ${slotToLabel(config, t)} · ${status}`}
                      className="absolute top-0 h-full hover:outline hover:outline-1 hover:outline-white/40"
                      style={{ left: t * CELL_W, width: CELL_W, ...cellStyle(status, w.priority) }} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-4 px-4 py-3 border-t border-line bg-surface2">
          <LegendItem color="rgba(48,209,88,0.28)" label="Covered" />
          <LegendItem color="rgba(255,69,58,0.9)" label="Uncovered · Critical" />
          <LegendItem color="rgba(255,69,58,0.4)" label="Uncovered · Low priority" />
          <span className="mono text-[10px] text-faint ml-auto">Higher priority gaps burn brighter red.</span>
        </div>
      </div>
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-3 h-3 rounded-sm border border-black/40" style={{ backgroundColor: color }} />
      <span className="text-[11px] text-muted">{label}</span>
    </div>
  );
}
